/**
 * Custom-experience registry (What's New Tier C).
 *
 * A tip's `experienceKey` selects a code-registered component to render inside the
 * What's New modal shell, instead of the default title/body card. Apps register
 * their experiences at startup; an unknown/absent key falls back to the default card.
 *
 * See docs/FeatureDesigns/whats-new-custom-experiences.md.
 */

import type { ComponentType } from 'react';
import type { WhatsNewTip } from './whatsNewApi';
import type { SurveyAnswers } from './surveyDefinition';

/** Props every experience component receives from the modal shell. */
export interface WhatsNewExperienceProps {
    tip: WhatsNewTip;
    /** Opaque params authored on the tip (`experienceData`). */
    data: Record<string, unknown> | undefined;
    /** Terminal: long (dismiss) cooldown — the user is done and shouldn't be nagged soon. */
    onDismiss: () => void;
    /** Soft: short (remind-later) cooldown. */
    onRemindLater: () => void;
    /** Terminal success (survey submitted, offer accepted) — dismiss cooldown, tracked as 'completed'. */
    onComplete: (meta?: unknown) => void;
    /**
     * Submit a survey response: persists the answers (as app_feedback) server-side,
     * then closes and arms the cooldown — the server records the tip 'completed'.
     * See docs/FeatureDesigns/whats-new-surveys.md.
     */
    submit: (answers: SurveyAnswers) => void;
    /** Fire an in-experience analytics event without closing. No-op until per-event capture ships. */
    onAction: (key: string, meta?: unknown) => void;
    /** Soft close (matches Escape/overlay) — remind-later cooldown. */
    close: () => void;
    /**
     * "I have nothing to show this user" — move to the next tip.
     *
     * NOT the same as `close`. A close is a user decision: it snoozes the tip
     * and arms the 24h per-device cooldown, which suppresses *every* tip until
     * tomorrow. An experience that discovers it has no data made no such
     * decision on the user's behalf, and letting it spend the day's What's New
     * slot means a student with thin data can be starved of every other tip,
     * every day. Skipping records nothing and costs nothing.
     */
    skip: () => void;
    /**
     * "My real content is on screen now" — beacons the impression and starts the
     * time-to-click clock. Only meaningful with `defersImpression`; idempotent,
     * so calling it on every render is fine.
     */
    shown: () => void;
}

export interface ExperienceManifest {
    component: ComponentType<WhatsNewExperienceProps>;
    /** true → the shell hides its default Dismiss / Remind footer; the experience renders its own controls. */
    ownsControls?: boolean;
    /** Override the modal heading (default "What's New"). */
    title?: string;
    /**
     * Set when the experience resolves data before it knows whether it has
     * anything to show. The shell then holds the impression beacon until the
     * experience calls `shown()`, instead of firing it the moment the tip is
     * selected — otherwise a card that loads and then `skip()`s still counts as
     * "shown", and `peopleShown` silently overstates reach.
     *
     * The failure mode is deliberately the safe direction: an experience that
     * sets this and never calls `shown()` records ZERO impressions, which is
     * loud in the Effectiveness tab. Over-counting is the one that hides.
     */
    defersImpression?: boolean;
}

const registry = new Map<string, ExperienceManifest>();

/** Register (or replace) an experience under `key`. Call at app startup. */
export function registerExperience(key: string, manifest: ExperienceManifest): void {
    registry.set(key, manifest);
}

/** Resolve a tip's experience, or undefined to fall back to the default card. */
export function getExperience(key: string | undefined): ExperienceManifest | undefined {
    return key ? registry.get(key) : undefined;
}

/** Keys currently registered (e.g. to populate an admin selector). */
export function registeredExperienceKeys(): string[] {
    return [...registry.keys()];
}
